import Draw from 'ol/interaction/Draw.js';
import Overlay from 'ol/Overlay.js';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style.js';
import { LineString } from 'ol/geom.js';
import { getLength } from 'ol/sphere.js';
import { unByKey } from 'ol/Observable.js';
import type { EventsKey } from 'ol/events';
import type VectorSource from 'ol/source/Vector';

import { ref, watch } from 'vue';
import type { Ref } from 'vue';

import { message } from 'ant-design-vue';

import { useI18n } from 'vue-i18n';

import type Map from 'ol/Map';

import MapHelpers from '@/components/map/utils/MapHelpers';


type OlMapWithMeasure = Pick<Map, 'addInteraction' | 'addOverlay' | 'getInteractions' | 'getOverlayById' | 'removeInteraction' | 'removeOverlay'>;

type MeasureSourceRef = Ref<VectorSource | null>;


let overlayId = 0;
let measureTooltipElement: HTMLDivElement | null;
let measureTooltipOverlay: Overlay | null;
let measureTooltipOverlays: number[] = [];

let isDrawing = false;

let clipboardStr = '';


type LengthFormatResult = {
	lengthText: string;
	latitude: number;
	longitude: number;
	forClipboard: string;
};


const formatLength = (line: LineString): LengthFormatResult | null => {
	const lonLat = MapHelpers.transformToLonLat(line.getLastCoordinate());
	const longitude = lonLat[0];
	const latitude = lonLat[1];
	if (longitude === undefined || latitude === undefined) {
		return null;
	}
	const length = getLength(line);
	let lengthText: string;
	if (length > 100) {
		lengthText = String(Math.round((length / 1000) * 100) / 100) + ' ' + 'km';
	} else {
		lengthText = String(Math.round(length * 100) / 100) + ' ' + 'm';
	}
	const forClipboard = 'length: ' + (length / 1000).toFixed(3) + ' km\nlat: ' + latitude.toFixed(5) + '\nlon: ' + longitude.toFixed(5);
	return { lengthText, latitude, longitude, forClipboard };
};


const setMeasureTooltipContent = (
	element: HTMLDivElement,
	{ lengthText, latitude, longitude }: Pick<LengthFormatResult, 'lengthText' | 'latitude' | 'longitude'>,
): void => {
	element.replaceChildren(
		document.createTextNode(lengthText),
		document.createElement('br'),
		document.createTextNode('lat: ' + latitude.toFixed(6)),
		document.createElement('br'),
		document.createTextNode('lon: ' + longitude.toFixed(6)),
	);
};


const style = new Style({
	fill: new Fill({
		color: 'rgba(255, 255, 255, 0.2)',
	}),
	stroke: new Stroke({
		color: 'rgba(0, 0, 0, 0.5)',
		width: 2,
	}),
	image: new CircleStyle({
		radius: 5,
		stroke: new Stroke({
			color: 'rgba(0, 0, 0, 0.7)',
		}),
		fill: new Fill({
			color: 'rgba(255, 255, 255, 0.2)',
		}),
	}),
});


const createMeasureTooltip = (map: OlMapWithMeasure): void => {
	if (measureTooltipElement != null && measureTooltipElement.parentNode != null) {
		measureTooltipElement.parentNode.removeChild(measureTooltipElement);
	}
	measureTooltipElement = document.createElement('div');
	measureTooltipElement.className = 'ol-tooltip ol-tooltip-measure';
	overlayId += 1;
	measureTooltipOverlays.push(overlayId);
	measureTooltipOverlay = new Overlay({
		id: overlayId,
		element: measureTooltipElement,
		offset: [0, -7],
		positioning: 'bottom-center',
		stopEvent: false,
		insertFirst: false,
	});
	map.addOverlay(measureTooltipOverlay);
};


const removeMeasure = (map: OlMapWithMeasure, source: VectorSource): void => {
	if (isDrawing) {
		map.getInteractions().forEach(interaction => {
			if (interaction instanceof Draw) {
				map.removeInteraction(interaction);
			}
		});
		source.clear();
		isDrawing = false;
		measureTooltipOverlays.forEach(currentOverlayId => {
			const overlay = map.getOverlayById(currentOverlayId);
			if (overlay != null) {
				map.removeOverlay(overlay);
			}
		});
		measureTooltipOverlays = [];
	}
};


const useMeasure = (olMap: Ref<OlMapWithMeasure | null>, measureEnabled: Ref<boolean>) => {
	const { t } = useI18n();
	const measureSource: MeasureSourceRef = ref(null);

	const updateClipboard = (): void => {
		if (navigator?.clipboard != null && typeof navigator.clipboard.writeText === 'function') {
			navigator.clipboard.writeText(clipboardStr);
			message.info(t('home-map.tools.measureHelpClipboard'));
			return;
		}
		const textarea = document.createElement('textarea');
		textarea.value = clipboardStr;
		document.body.appendChild(textarea);
		textarea.select();
		try {
			document.execCommand('copy');
			message.info(t('home-map.tools.measureHelpClipboard'));
		} catch {
			// ignore
		}
		document.body.removeChild(textarea);
	};

	const addMeasure = (map: OlMapWithMeasure, source: VectorSource): void => {
		const draw = new Draw({
			source: source,
			type: 'LineString',
			style: feature => {
				const geometry = feature.getGeometry();
				if (geometry == null) {
					return undefined;
				}
				const geometryType = geometry.getType();
				if (geometryType === 'LineString' || geometryType === 'Point') {
					return style;
				}
				return undefined;
			},
		});
		createMeasureTooltip(map);
		let listener: EventsKey | undefined;
		draw.on('drawstart', evt => {
			const geometry = evt.feature.getGeometry();
			if (geometry == null) {
				return;
			}
			listener = geometry.on('change', changeEvent => {
				const geom = changeEvent.target;
				if (!(geom instanceof LineString)) {
					return;
				}
				const lengthFormat = formatLength(geom);
				if (lengthFormat != null && measureTooltipElement != null) {
					setMeasureTooltipContent(measureTooltipElement, lengthFormat);
					clipboardStr = lengthFormat.forClipboard;
				}
				measureTooltipOverlay?.setPosition(geom.getLastCoordinate());
			});
		});
		draw.on('drawend', () => {
			updateClipboard();
			if (measureTooltipElement != null) {
				measureTooltipElement.className = 'ol-tooltip ol-tooltip-static';
			}
			measureTooltipElement = null;
			createMeasureTooltip(map);
			if (listener != null) {
				unByKey(listener);
			}
		});
		map.addInteraction(draw);
		isDrawing = true;
	};

	watch(measureEnabled, value => {
		const mapInstance = olMap.value;
		const source = measureSource.value;
		if (mapInstance == null || source == null) {
			return;
		}
		if (value) {
			message.info(t('home-map.tools.measureHelp'));
			addMeasure(mapInstance, source);
		} else {
			removeMeasure(mapInstance, source);
		}
	});
	return {
		measureSource,
	};
};


export {
	useMeasure,
};
